import { useMemo } from 'react';
import { cn } from '@/lib/utils/cn';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps, QuestionOption } from '@/types';

interface LikertConfig {
  options?: QuestionOption[];
  likertPreset?: string;
  showLabels?: boolean;
  layout?: 'horizontal' | 'vertical';
}

// Preset scale libraries
const LIKERT_PRESETS: Record<string, QuestionOption[]> = {
  agreement_5: [
    { id: '1', text: 'Strongly Disagree', value: '1', order: 1 },
    { id: '2', text: 'Disagree', value: '2', order: 2 },
    { id: '3', text: 'Neutral', value: '3', order: 3 },
    { id: '4', text: 'Agree', value: '4', order: 4 },
    { id: '5', text: 'Strongly Agree', value: '5', order: 5 },
  ],
  agreement_7: [
    { id: '1', text: 'Strongly Disagree', value: '1', order: 1 },
    { id: '2', text: 'Disagree', value: '2', order: 2 },
    { id: '3', text: 'Somewhat Disagree', value: '3', order: 3 },
    { id: '4', text: 'Neutral', value: '4', order: 4 },
    { id: '5', text: 'Somewhat Agree', value: '5', order: 5 },
    { id: '6', text: 'Agree', value: '6', order: 6 },
    { id: '7', text: 'Strongly Agree', value: '7', order: 7 },
  ],
  satisfaction_5: [
    { id: '1', text: 'Very Dissatisfied', value: '1', order: 1 },
    { id: '2', text: 'Dissatisfied', value: '2', order: 2 },
    { id: '3', text: 'Neutral', value: '3', order: 3 },
    { id: '4', text: 'Satisfied', value: '4', order: 4 },
    { id: '5', text: 'Very Satisfied', value: '5', order: 5 },
  ],
  frequency_5: [
    { id: '1', text: 'Never', value: '1', order: 1 },
    { id: '2', text: 'Rarely', value: '2', order: 2 },
    { id: '3', text: 'Sometimes', value: '3', order: 3 },
    { id: '4', text: 'Often', value: '4', order: 4 },
    { id: '5', text: 'Always', value: '5', order: 5 },
  ],
  importance_5: [
    { id: '1', text: 'Not Important', value: '1', order: 1 },
    { id: '2', text: 'Slightly Important', value: '2', order: 2 },
    { id: '3', text: 'Moderately Important', value: '3', order: 3 },
    { id: '4', text: 'Important', value: '4', order: 4 },
    { id: '5', text: 'Very Important', value: '5', order: 5 },
  ],
  likelihood_5: [
    { id: '1', text: 'Very Unlikely', value: '1', order: 1 },
    { id: '2', text: 'Unlikely', value: '2', order: 2 },
    { id: '3', text: 'Neutral', value: '3', order: 3 },
    { id: '4', text: 'Likely', value: '4', order: 4 },
    { id: '5', text: 'Very Likely', value: '5', order: 5 },
  ],
};

const DEFAULT_LIKERT_OPTIONS = LIKERT_PRESETS.agreement_5;

export function Likert({
  question,
  value,
  onChange,
  disabled,
  errors,
}: QuestionComponentProps<string | null>) {
  const config = question.config as LikertConfig | undefined;

  // Get options from config or preset
  const options = useMemo(() => {
    // If custom options are provided, use them
    if (config?.options && config.options.length > 0) {
      return config.options;
    }
    // If a preset is specified, use it
    const preset = config?.likertPreset;
    if (preset && LIKERT_PRESETS[preset]) {
      return LIKERT_PRESETS[preset];
    }
    // Default to 5-point agreement scale
    return DEFAULT_LIKERT_OPTIONS;
  }, [config?.options, config?.likertPreset]);

  const showLabels = config?.showLabels ?? true;
  const layout = config?.layout || 'horizontal';

  const handleSelect = (optionValue: string) => {
    if (disabled) return;
    // Allow deselection
    if (value === optionValue) {
      onChange(null);
    } else {
      onChange(optionValue);
    }
  };

  // Determine if we should show compact numeric display or full labels
  const isCompact = options.length > 5;

  return (
    <QuestionWrapper question={question} errors={errors}>
      <div className="overflow-x-auto">
        {/* Mobile: Stack vertically */}
        <div className={cn('sm:hidden space-y-2', layout === 'horizontal' && 'hidden')}>
          {options.map((option) => (
            <label
              key={option.id}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                value === option.value
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="radio"
                name={question.code}
                value={option.value}
                checked={value === option.value}
                onChange={() => handleSelect(option.value)}
                disabled={disabled}
                className="h-5 w-5 border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-900 flex-1">
                {option.text}
              </span>
            </label>
          ))}
        </div>

        {/* Desktop: Horizontal scale */}
        <div className={cn('hidden sm:block', layout === 'vertical' && '!hidden')}>
          {/* Labels row (if showing full labels) */}
          {showLabels && !isCompact && (
            <div className="flex justify-between mb-2">
              {options.map((option) => (
                <div
                  key={option.id}
                  className="flex-1 text-center text-xs text-gray-500 px-1"
                  style={{ minWidth: '80px' }}
                >
                  {option.text}
                </div>
              ))}
            </div>
          )}

          {/* Scale buttons */}
          <div className="flex justify-center gap-2 flex-wrap">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => handleSelect(option.value)}
                disabled={disabled}
                className={cn(
                  'flex flex-col items-center rounded-lg border-2 transition-all',
                  isCompact ? 'px-3 py-2 min-w-[50px]' : 'px-4 py-3 min-w-[100px]',
                  value === option.value
                    ? 'border-primary-600 bg-primary-50 text-primary-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300 hover:bg-primary-50/50',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                <span className={cn('font-medium', isCompact ? 'text-base' : 'text-lg mb-1')}>
                  {option.value}
                </span>
                {!isCompact && showLabels && (
                  <span className="text-xs text-center leading-tight">
                    {option.text}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Endpoint labels for compact mode */}
          {isCompact && showLabels && options.length >= 2 && (
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>{options[0].text}</span>
              <span>{options[options.length - 1].text}</span>
            </div>
          )}
        </div>

        {/* Mobile accordion style (always visible on mobile) */}
        <div className={cn('sm:hidden', layout === 'vertical' && '!block')}>
          {layout === 'horizontal' && (
            <div className="space-y-2">
              {options.map((option) => (
                <label
                  key={option.id}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                    value === option.value
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <input
                    type="radio"
                    name={question.code}
                    value={option.value}
                    checked={value === option.value}
                    onChange={() => handleSelect(option.value)}
                    disabled={disabled}
                    className="h-5 w-5 border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-900 flex-1">
                    {option.text}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Selection indicator */}
        {value !== null && (
          <p className="text-sm text-gray-600 mt-3 text-center sm:text-left">
            Selected: <span className="font-medium">{options.find(o => o.value === value)?.text}</span>
          </p>
        )}
      </div>
    </QuestionWrapper>
  );
}
